import React, { useState } from 'react';
import { useModalDismiss } from '../hooks/useModalDismiss';

export interface ImportBookItem {
  title: string;
  isDuplicate: boolean;
  newTitle: string;
}

interface BatchImportManagerProps {
  books: ImportBookItem[];
  existingTitles: string[];
  onConfirm: (updatedBooks: ImportBookItem[]) => void;
  onCancel: () => void;
}

export function BatchImportManager({ books, existingTitles, onConfirm, onCancel }: BatchImportManagerProps) {
  const [items, setItems] = useState<ImportBookItem[]>(books);
  const [errors, setErrors] = useState<Record<number, string>>({});
  const dismiss = useModalDismiss(onCancel);

  const handleTitleChange = (index: number, newTitle: string) => {
    const updated = [...items];
    updated[index].newTitle = newTitle;
    
    // 检查新标题是否与其他已有标题重复
    const newErrors = { ...errors };
    const trimmedTitle = newTitle.trim();
    
    if (!trimmedTitle) {
      newErrors[index] = '作品名称不能为空';
    } else if (existingTitles.includes(trimmedTitle)) {
      newErrors[index] = '此名称已存在，请选择其他名称';
    } else {
      // 检查是否与本批导入中的其他作品重复
      const duplicateInBatch = updated.find((item, i) => i !== index && item.newTitle.trim() === trimmedTitle);
      if (duplicateInBatch) {
        newErrors[index] = '此名称与本批导入中的其他作品重复';
      } else {
        delete newErrors[index];
      }
    }
    
    setErrors(newErrors);
    setItems(updated);
  };

  const handleConfirm = () => {
    // 验证所有项
    const newErrors: Record<number, string> = {};
    let hasError = false;

    items.forEach((item, index) => {
      const trimmedTitle = item.newTitle.trim();
      if (!trimmedTitle) {
        newErrors[index] = '作品名称不能为空';
        hasError = true;
      } else if (existingTitles.includes(trimmedTitle)) {
        newErrors[index] = '此名称已存在';
        hasError = true;
      } else {
        const duplicateInBatch = items.find((i, idx) => idx !== index && i.newTitle.trim() === trimmedTitle);
        if (duplicateInBatch) {
          newErrors[index] = '与本批导入中的其他作品重复';
          hasError = true;
        }
      }
    });

    if (hasError) {
      setErrors(newErrors);
      return;
    }

    onConfirm(items);
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'var(--modal-mask)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1001,
      backdropFilter: 'blur(4px)', overflowY: 'auto', padding: '20px'
    }} {...dismiss}>
      <div style={{
        backgroundColor: 'var(--card-bg)', padding: 32, borderRadius: 24, width: 580,
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', color: 'var(--text-color)',
        margin: 'auto'
      }}>
        <h2 style={{ marginBottom: 12, fontSize: 18, fontWeight: 800 }}>批量导入作品</h2>
        <p style={{ marginBottom: 20, color: 'var(--text-secondary)', fontSize: 13 }}>
          共 {books.length} 部作品。请检查名称重复的作品并为其设置新名称。
        </p>

        <div style={{
          maxHeight: '400px', overflowY: 'auto', marginBottom: 24,
          border: '1px solid var(--border-color)', borderRadius: 12, padding: 16,
          backgroundColor: 'var(--bg-color)'
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {items.map((item, index) => (
              <div key={index}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                  <span style={{
                    fontSize: 12, fontWeight: 700, padding: '4px 10px', borderRadius: 6,
                    backgroundColor: item.isDuplicate ? 'rgba(255, 77, 79, 0.15)' : 'rgba(52, 211, 153, 0.15)',
                    color: item.isDuplicate ? 'var(--danger-color)' : 'var(--success-color)'
                  }}>
                    {item.isDuplicate ? '❌ 名称重复' : '✅ 名称正常'}
                  </span>
                  <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                    原名称: <span style={{ color: 'var(--text-color)', fontWeight: 500 }}>{item.title}</span>
                  </span>
                </div>
                <input
                  type="text"
                  value={item.newTitle}
                  onChange={(e) => handleTitleChange(index, e.target.value)}
                  placeholder="新名称"
                  style={{
                    width: '100%', padding: '10px 14px', boxSizing: 'border-box',
                    border: errors[index] ? '2px solid var(--danger-color)' : '2px solid var(--border-color)',
                    backgroundColor: 'var(--card-bg)', color: 'var(--text-color)',
                    borderRadius: 8, fontSize: 14, transition: 'border-color 0.2s'
                  }}
                />
                {errors[index] && (
                  <div style={{ color: 'var(--danger-color)', fontSize: 12, marginTop: 6, fontWeight: 500 }}>
                    {errors[index]}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
          <button
            onClick={onCancel}
            style={{
              padding: '12px 24px', backgroundColor: 'transparent', color: 'var(--text-secondary)',
              border: 'none', borderRadius: 12, cursor: 'pointer', fontSize: 15, fontWeight: 600
            }}
          >
            取消
          </button>
          <button
            onClick={handleConfirm}
            style={{
              padding: '12px 32px', backgroundColor: 'var(--primary-color)', color: 'white',
              border: 'none', borderRadius: 12, cursor: 'pointer', fontSize: 15, fontWeight: 700,
              boxShadow: '0 10px 15px -3px rgba(24, 144, 255, 0.4)'
            }}
          >
            确认导入
          </button>
        </div>
      </div>
    </div>
  );
}
